import React, { useCallback, useEffect, useState } from "react";
import { PILLARS, type Pillar, type Weights } from "@nexus/contracts";
import { PILLAR_METADATA } from "../types";
import { Sliders, RotateCcw, Loader2 } from "lucide-react";

interface WeightSlidersProps {
  weights: Weights;
  onChange: (weights: Weights) => void;
  onReset: () => void;
  isSimulating: boolean;
}

const DEFAULT_WEIGHTS: Weights = {
  AI: 0.25,
  CHIP: 0.25,
  ENERGY: 0.25,
  ROBOTICS: 0.25,
};

export const WeightSliders: React.FC<WeightSlidersProps> = ({
  weights,
  onChange,
  onReset,
  isSimulating,
}) => {
  // Local weight state for instantaneous slider drag
  const [localWeights, setLocalWeights] = useState<Weights>(weights);

  useEffect(() => {
    setLocalWeights(weights);
  }, [weights]);

  // When a slider moves, renormalize other sliders so the sum equals 1.0
  const handleSliderChange = (pillar: Pillar, rawValue: number) => {
    const newVal = Math.max(0, Math.min(1, rawValue));
    const otherPillars = PILLARS.filter((p) => p !== pillar);
    const otherSum = otherPillars.reduce((sum, p) => sum + (localWeights[p] || 0), 0);

    const updated: Record<Pillar, number> = { ...localWeights, [pillar]: newVal };

    if (otherSum === 0) {
      // If all others were 0, distribute remainder equally
      const remainder = 1 - newVal;
      const share = remainder / otherPillars.length;
      for (const p of otherPillars) {
        updated[p] = share;
      }
    } else {
      const remainingTarget = 1 - newVal;
      for (const p of otherPillars) {
        const ratio = (localWeights[p] || 0) / otherSum;
        updated[p] = Number((ratio * remainingTarget).toFixed(4));
      }
    }

    // Ensure strict sum equals 1.0 within tolerance
    const currentSum = PILLARS.reduce((acc, p) => acc + updated[p], 0);
    const diff = 1 - currentSum;
    updated[otherPillars[0]] = Number((updated[otherPillars[0]] + diff).toFixed(4));

    setLocalWeights(updated as Weights);
    debouncedTrigger(updated as Weights);
  };

  // Debounced parent trigger for API simulation
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const debouncedTrigger = useCallback(
    (newWeights: Weights) => {
      if (timer) clearTimeout(timer);
      const newTimer = setTimeout(() => {
        onChange(newWeights);
      }, 250);
      setTimer(newTimer);
    },
    [timer, onChange],
  );

  const isCustomized = PILLARS.some(
    (p) => Math.abs((localWeights[p] ?? 0.25) - 0.25) > 0.01,
  );

  return (
    <div className="panel-section">
      <div className="section-title">
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Sliders size={13} color="var(--accent-cyan)" />
          <span>PILLAR WEIGHTS (SIMULATOR)</span>
        </div>
        {isSimulating ? (
          <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--accent-cyan)", fontSize: "10px" }}>
            <Loader2 size={11} className="animate-spin" />
            <span>계산 중...</span>
          </div>
        ) : isCustomized ? (
          <span style={{ color: "var(--accent-amber)", fontSize: "10px", fontWeight: 600 }}>
            사용자 정의 (POST /api/index/simulate)
          </span>
        ) : (
          <span style={{ color: "var(--text-muted)", fontSize: "10px" }}>
            기본 균형 (각 25%)
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {PILLARS.map((pillar) => {
          const meta = PILLAR_METADATA[pillar];
          const val = localWeights[pillar] ?? 0.25;
          const pct = Math.round(val * 100);

          return (
            <div key={pillar} className="weight-slider-row">
              <div className="weight-slider-header">
                <span className="pillar-name" style={{ color: meta.color }}>
                  <span
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: meta.color,
                      display: "inline-block",
                    }}
                  />
                  {meta.nameKo} ({pillar})
                </span>
                <span className="pillar-val tabular-nums">{pct}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={val}
                onChange={(e) => handleSliderChange(pillar, parseFloat(e.target.value))}
                style={
                  {
                    accentColor: meta.color,
                  } as React.CSSProperties
                }
              />
            </div>
          );
        })}
      </div>

      <div className="weight-actions">
        <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>
          합계: <strong className="font-mono" style={{ color: "#f1f5f9" }}>100% (1.00)</strong>
        </span>
        <button
          className="btn-secondary"
          onClick={() => {
            setLocalWeights(DEFAULT_WEIGHTS);
            onReset();
          }}
          style={{ display: "flex", alignItems: "center", gap: "4px" }}
          title="모든 가중치를 25% 균등 분배로 재설정"
        >
          <RotateCcw size={11} /> 25% 균등 초기화
        </button>
      </div>
    </div>
  );
};
